import { useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import Avatar from '@material-ui/core/Avatar';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemText from '@material-ui/core/ListItemText';
import AssignmentIcon from '@material-ui/icons/Assignment';
import BusinessIcon from '@material-ui/icons/Business';
import DescriptionIcon from '@material-ui/icons/Description';
import EcoIcon from '@material-ui/icons/Eco';
import GradeIcon from '@material-ui/icons/Grade';
import InfoIcon from '@material-ui/icons/Info';
import PeopleAltIcon from '@material-ui/icons/PeopleAlt';
import SupervisorAccountIcon from '@material-ui/icons/SupervisorAccount';
import Button from 'components/StyledButton/StyledButton';
import { ReduxState } from 'redux/types';
import { useDialog } from 'utils/dialog';

import Company from '../../../../types/Company';
import api from '../../../../utils/api';
import snackbar from '../../../../utils/snackbar';
import CompanyLocationsDialog from '../../../CompanyLocationsDialog/CompanyLocationsDialog';
import CompanyUsersDialog from '../../../CompanyUsersDialog/CompanyUsersDialog';
import UpdateStateDialog from '../UpdateStateDialog/UpdateStateDialog';
import { StyledGreenAvatar, StyledList, StyledListFullWidth, StyledListItem } from './styled';

/*
 * Footer of the company details dialog;
 * it includes the button for see and modify the users, auditors, locations and certificates.
 */

interface Props {
    company: Company;
    updateData: () => void;
    handleAttributeChange: (attribute: string, value: any) => void;
    isLoading?: boolean;
}

const Footer = (props: Props) => {
    const { company, updateData, handleAttributeChange, isLoading } = props;
    const { auditors, certifiers, id: companyId, certificates, locations, state, users, certifiedCo2Saved, certifiedPlasticReduced } = company;

    const { t } = useTranslation();
    const history = useHistory();

    const translatedState = t('dialogs.company_details.states.' + state);

    const [isStateLoading, setIsStateLoading] = useState(false);
    const [isUsersDialogOpen, setIsUsersDialogOpen] = useState(false);
    const [userDialogType, setUserDialogType] = useState<'customer' | 'auditor' | 'certifier'>('customer');

    const userType = useSelector((s: ReduxState) => s.user.type);
    const allowedStateChanges = useSelector((s: ReduxState) => s.user.allowedStateChanges);

    const nextStateChanges = allowedStateChanges.filter((x: any) => {
        return (x.users ?? []).includes(userType) && x.from === state;
    });

    const [isCompanyLocationsDialogOpen, setIsCompanyLocationsDialogOpen] = useState(false);
    const [isUpdateStateDialogOpen, setIsUpdateStateDialogOpen] = useState(false);

    const countUsers = users.length;
    const countCertifiers = certifiers.length;
    const countAuditors = auditors.length;
    const countLocations = locations.length;
    const lastCertificate = certificates.length ? certificates[certificates.length - 1] : null;

    const certifiedCo2SavedFormatted = certifiedCo2Saved === null ? 'n.d.' : (!certifiedCo2Saved ? certifiedCo2Saved : (certifiedCo2Saved * -1));
    const certifiedPlasticReducedFormatted = certifiedPlasticReduced === null ? 'n.d.' : (!certifiedPlasticReduced ? certifiedPlasticReduced : (certifiedPlasticReduced * -1));

    const monthlyPlastic = company?.chartData?.pa?.kgPlastic;
    const monthlyCo2 = company?.chartData?.pa?.kgCo2;

    const [openDialog, closeDialog] = useDialog();

    const updateState = useCallback((newState: string) => {
        if (newState === 'audit_completed') {
            openDialog({
                title: t('dialogs.audit_completed_confirmation.title'),
                message: t('dialogs.audit_completed_confirmation.message'),
                actions: [
                    {
                        text: t('dialogs.generic.no'),
                        color: 'secondary',
                        action: closeDialog
                    },
                    {
                        text: t('dialogs.generic.yes'),
                        color: 'primary',
                        action: () => {
                            api.request('/companies/' + companyId + '/state', 'PUT', { state: newState }).then(res => {
                                if (res.state === newState) {
                                    snackbar.success(t('dialogs.company_details.state_changed'));
                                }
                                else {
                                    snackbar.success(t('dialogs.company_details.state_change_error'));
                                }
                                handleAttributeChange('state', res.state);
                            }).finally(() => {
                                setIsStateLoading(false);
                            });
                            closeDialog();
                        }
                    }
                ]
            });

            return;
        }

        api.request('/companies/' + companyId + '/state', 'PUT', { state: newState }).then(res => {
            if (res.state === newState) {
                snackbar.success(t('dialogs.company_details.state_changed'));
            }
            else {
                snackbar.success(t('dialogs.company_details.state_change_error'));
            }
            handleAttributeChange('state', res.state);
        }).finally(() => {
            setIsStateLoading(false);
        });
    }, [companyId, handleAttributeChange, t, openDialog, closeDialog]);

    const openUsersDialog = () => {
        setUserDialogType('customer');
        setIsUsersDialogOpen(true);
    };

    const openAuditorsDialog = () => {
        setUserDialogType('auditor');
        setIsUsersDialogOpen(true);
    };

    const openCertifierDialog = () => {
        setUserDialogType('certifier');
        setIsUsersDialogOpen(true);
    };

    const redirectToPage = useCallback((page: string) => {
        history.push('/companies#' + companyId);
        history.push(page);
    }, [companyId, history]);

    return (
        <>
            <StyledListFullWidth>
                <
                    // @ts-ignore https://github.com/mui-org/material-ui/issues/14971
                    StyledListItem
                >
                    <ListItemAvatar>
                        <StyledGreenAvatar>
                            <InfoIcon />
                        </StyledGreenAvatar>
                    </ListItemAvatar>

                    <ListItemText primary={(
                        <div style={{ display: 'flex' }}>
                            <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <div style={{ whiteSpace: 'nowrap', marginRight: '24px' }}>
                                    <Trans i18nKey='dialogs.company_details.state' tOptions={{ state: translatedState }}>
                                        _<strong>{{ translatedState }}</strong>_
                                    </Trans>
                                </div>
                            </div>

                            <div>
                                {nextStateChanges.map((stateChange: any) => {
                                    return (
                                        <Button size='small' variant='outlined' style={{ display: 'block', margin: '6px 0', width: '100%' }} onClick={() => updateState(stateChange.to)} disabled={isLoading || isStateLoading} >
                                            {stateChange.label ? (
                                                t(stateChange.label)
                                            ) : (
                                                t('dialogs.company_details.switch_state', { state: t('dialogs.company_details.states.' + stateChange.to) })
                                            )}

                                        </Button>
                                    );
                                })}

                                {(userType === 'admin' || userType === 'certifier') && (
                                    <Button size='small' variant='contained' color='primary' style={{ display: 'block', margin: '6px 0', width: '100%' }} onClick={() => setIsUpdateStateDialogOpen(true)} disabled={isLoading || isStateLoading} >
                                        {t('dialogs.company_details.edit_state')}
                                    </Button>
                                )}
                            </div>
                        </div>
                    )} />
                </StyledListItem>
            </StyledListFullWidth>

            <StyledList>
                <StyledListItem button onClick={openUsersDialog} disabled={isLoading} >
                    <ListItemAvatar>
                        <Avatar>
                            <PeopleAltIcon />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={(
                        <Trans i18nKey='dialogs.company_details.number_of_users' count={countUsers}>
                            _<strong>{{ countUsers }}</strong>_
                        </Trans>
                    )} />
                </StyledListItem>

                <StyledListItem button onClick={openCertifierDialog} disabled={isLoading} >
                    <ListItemAvatar>
                        <Avatar>
                            <SupervisorAccountIcon />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={(
                        <Trans i18nKey='dialogs.company_details.number_of_certifiers' count={countCertifiers}>
                            _<strong>{{ countCertifiers }}</strong>_
                        </Trans>
                    )} />
                </StyledListItem>

                <StyledListItem button onClick={openAuditorsDialog} disabled={isLoading} >
                    <ListItemAvatar>
                        <Avatar>
                            <SupervisorAccountIcon />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={(
                        <Trans i18nKey='dialogs.company_details.number_of_auditors' count={countAuditors}>
                            _<strong>{{ countAuditors }}</strong>_
                        </Trans>
                    )} />
                </StyledListItem>
            </StyledList>

            <StyledList>
                <StyledListItem button onClick={() => setIsCompanyLocationsDialogOpen(true)} disabled={isLoading} >
                    <ListItemAvatar>
                        <Avatar>
                            <BusinessIcon />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={(
                        <Trans i18nKey='dialogs.company_details.number_of_locations' count={countLocations}>
                            _<strong>{{ countLocations }}</strong>_
                        </Trans>
                    )} />
                </StyledListItem>

                <StyledListItem button onClick={() => redirectToPage('/companies/' + companyId + '/certificates')} disabled={isLoading} >
                    <ListItemAvatar>
                        <Avatar>
                            <GradeIcon />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={(
                        lastCertificate ? (
                            <Trans i18nKey='dialogs.company_details.certificate_grade' tOptions={{ grade: lastCertificate.grade }} >
                                _<strong>{lastCertificate.grade}</strong>_
                            </Trans>
                        ) : (
                            <Trans i18nKey='dialogs.company_details.no_certificate'></Trans>
                        )
                    )} />
                </StyledListItem>
            </StyledList>

            <StyledList>
                <StyledListItem button onClick={() => redirectToPage('/companies/' + companyId + '/plastic-assessments')} disabled={isLoading} >
                    <ListItemAvatar>
                        <Avatar>
                            <AssignmentIcon />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={t('dialogs.company_details.monitoring_files')} />
                </StyledListItem>

                <StyledListItem button onClick={() => redirectToPage('/companies/' + companyId + '/documents')} disabled={isLoading} >
                    <ListItemAvatar>
                        <Avatar>
                            <DescriptionIcon />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={t('dialogs.company_details.documents')} />
                </StyledListItem>
            </StyledList>

            <StyledList>
                {/* @ts-ignore */}
                <StyledListItem>
                    <ListItemAvatar>
                        <Avatar>
                            <EcoIcon />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={(
                        <Trans i18nKey='dialogs.company_details.monthly_plastic' tOptions={{ amount: monthlyPlastic }}>
                            _<strong>{monthlyPlastic}</strong>_
                        </Trans>
                    )} />
                </StyledListItem>
            </StyledList>

            <StyledList>
                {/* @ts-ignore */}
                <StyledListItem>
                    <ListItemAvatar>
                        <Avatar>
                            <EcoIcon />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={(
                        <Trans i18nKey='dialogs.company_details.monthly_co2' tOptions={{ amount: monthlyCo2 }}>
                            _<strong>{monthlyCo2}</strong>_
                        </Trans>
                    )} />
                </StyledListItem>
            </StyledList>

            <StyledList>
                {/* @ts-ignore */}
                <StyledListItem>
                    <ListItemAvatar>
                        <Avatar>
                            <EcoIcon />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={(
                        <Trans i18nKey='dialogs.company_details.certified_plastic_reduced' tOptions={{ amount: certifiedPlasticReducedFormatted }}>
                            _<strong>{certifiedPlasticReducedFormatted}</strong>_
                        </Trans>
                    )} />
                </StyledListItem>
            </StyledList>

            <StyledList>
                {/* @ts-ignore */}
                <StyledListItem>
                    <ListItemAvatar>
                        <Avatar>
                            <EcoIcon />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={(
                        <Trans i18nKey='dialogs.company_details.certified_co2_saving' tOptions={{ co2: certifiedCo2SavedFormatted }}>
                            _<strong>{certifiedCo2SavedFormatted}</strong>_
                        </Trans>
                    )} />
                </StyledListItem>
            </StyledList>

            <StyledList>
                <StyledListItem button onClick={() => redirectToPage('/companies/' + companyId + '/gps')} disabled={isLoading} >
                    <ListItemAvatar>
                        <Avatar>
                            <GradeIcon />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={t('dialogs.company_details.gps')} />
                </StyledListItem>
            </StyledList>

            <CompanyUsersDialog
                open={isUsersDialogOpen}
                onClose={() => setIsUsersDialogOpen(false)}
                companyId={companyId}
                users={userDialogType === 'customer' ? users : (userDialogType === 'auditor' ? auditors : certifiers)}
                type={userDialogType}
                updateData={updateData}
                isLoading={isLoading}
            />

            <CompanyLocationsDialog
                open={isCompanyLocationsDialogOpen}
                onClose={() => setIsCompanyLocationsDialogOpen(false)}
                companyId={companyId}
                locations={locations}
                updateData={updateData}
                isLoading={isLoading}
            />

            <UpdateStateDialog
                open={isUpdateStateDialogOpen}
                onClose={() => setIsUpdateStateDialogOpen(false)}
                companyId={companyId}
                oldState={company.state}
                handleAttributeChange={handleAttributeChange}
            />
        </>
    );
};

export default Footer;
